import { isEqual } from 'lodash-es';
import get from 'platform/utilities/data/get';
import set from 'platform/utilities/data/set';

import { upgradeLegacyKeys } from './reconcile-disability-map';

import { sweepOrphanedTEConditions } from './reconcile-toxic-exposure-conditions';
import {
  getCurrentDisabilityIdArray,
  reconcileSelectedDisabilities,
} from './reconcile-supporting-evidence';

// Add new disability-referencing answers here and to the reconcile step below,
// rather than in a new module.
const REFERENCE_PATHS = [
  'toxicExposure.conditions',
  'view:isPow.powDisabilities',
  'vaTreatmentFacilities',
  'providerFacility',
];

const FACILITY_PATHS = ['vaTreatmentFacilities', 'providerFacility'];

// Saved forms can still hold keys from before the side of body was part of
// them. Upgrade before the sweep below, which would drop them as orphans.
const upgradeSelectionKeys = formData => {
  const rows = Array.isArray(formData?.newDisabilities)
    ? formData.newDisabilities
    : [];
  if (rows.length === 0) return formData;

  let next = formData;

  const pow = get('view:isPow.powDisabilities', next);
  const upgradedPow = upgradeLegacyKeys(pow, rows);
  if (upgradedPow !== pow) {
    next = set('view:isPow.powDisabilities', upgradedPow, next);
  }

  FACILITY_PATHS.forEach(path => {
    const facilities = get(path, next);
    if (!Array.isArray(facilities)) return;

    let touched = false;
    const updated = facilities.map(facility => {
      const names = facility?.treatedDisabilityNames;
      const upgraded = upgradeLegacyKeys(names, rows);
      if (upgraded === names) return facility;

      touched = true;
      return set('treatedDisabilityNames', upgraded, facility);
    });

    if (touched) next = set(path, updated, next);
  });

  return next;
};

// Returns the same object when nothing changed, so callers can skip the write.
export const reconcileDisabilityReferences = formData => {
  if (!formData) return formData;

  // Also upgrades answers keyed on the condition name alone.
  let next = sweepOrphanedTEConditions(formData);
  next = upgradeSelectionKeys(next);

  // An empty claimed set means conditions aren't populated yet; sweeping
  // against it would clear answers the Veteran still needs.
  if (getCurrentDisabilityIdArray(next).length > 0) {
    next = reconcileSelectedDisabilities(undefined, next);
  }

  const changed = REFERENCE_PATHS.some(
    path => !isEqual(get(path, formData), get(path, next)),
  );

  return changed ? next : formData;
};
